import {createSelector} from '@reduxjs/toolkit';
import type {RootState} from 'src/redux/store';
import {
  Account,
  Identifier,
  IdentifierTypes,
} from '../../types/serverTypes/Account';

export const selectAccountSlice = (state: RootState) => state.account;

export const selectAccount = createSelector(
  [selectAccountSlice],
  (accountState) => accountState.account,
);

export const selectAccountId = createSelector(
  [selectAccount],
  (account) => account.id || '',
);

export const selectIsLoggedIn = createSelector([selectAccountId], (id) =>
  Boolean(id && id !== '' && id !== '-1'),
);

export const selectUsername = createSelector(
  [selectAccount],
  (account) => account?.userName || '',
);

export const selectPinRequired = createSelector(
  [selectAccountSlice],
  (state) => state.pinRequired || false,
);

export const selectFirstName = createSelector(
  [selectAccount],
  (account) => account?.firstName || '',
);

export const selectLastName = createSelector(
  [selectAccount],
  (account) => account?.lastName || '',
);

export const selectFullName = createSelector(
  [selectFirstName, selectLastName],
  (first, last) => {
    const firstName = first === 'null' ? '' : first;
    const lastName = last === 'null' ? '' : last;
    return `${firstName} ${lastName}`.trim();
  },
);

export const selectGender = createSelector(
  [selectAccount],
  (account) => account?.demographics?.gender || '',
);

export const selectJobTitle = createSelector(
  [selectAccount],
  (account) => account?.demographics?.jobTitle || '',
);

export const selectBirthMonth = createSelector([selectAccount], (account) => {
  const month = account?.demographics?.birthMonth;
  return month && month !== 0 ? String(month).padStart(2, '0') : '';
});

export const selectBirthYear = createSelector([selectAccount], (account) =>
  String(account?.demographics?.birthYear || ''),
);

export const selectLocale = createSelector(
  [selectAccount],
  (account) => account?.locale || '',
);

export const selectOrgName = createSelector(
  [selectAccount],
  (account) => account?.orgName || '',
);

export const selectOrgId = createSelector(
  [selectAccount],
  (account) => account?.orgId || '',
);

export const selectConsumerIdentifier = createSelector(
  [selectAccount, (_, type?: IdentifierTypes) => type],
  (account: Account, type) => {
    const identifiers = account?.identifiers || [];

    if (!identifiers.length) return null;

    if (type) {
      return getLastIdentifier(identifiers, type);
    }

    // Default fallback priority
    return (
      getLastIdentifier(identifiers, IdentifierTypes.ConsumerRole) ||
      getLastIdentifier(identifiers, IdentifierTypes.ScanCode) ||
      getLastIdentifier(identifiers, IdentifierTypes.QrCode) ||
      null
    );
  },
);

export const selectPrivacyVersionResponse = createSelector(
  [selectAccountSlice],
  (state) => state.privacyVersionResponse || null,
);

function getLastIdentifier(identifiers: Identifier[], type: IdentifierTypes) {
  return (
    identifiers
      .filter((item) => item.type === type)
      .reduce(
        (latest, current) =>
          !latest ||
          new Date(current?.lastUpdated) > new Date(latest?.lastUpdated)
            ? current
            : latest,
        null,
      ) ?? null
  );
}
